import { useEffect, useRef } from 'react'

type Options = {
  /** When false the preview stream is stopped. */
  enabled: boolean
  video: boolean
  audio: boolean
  muted: boolean
  cameraOff: boolean
}

/**
 * Local camera/mic preview for the group-call lobby (before Chime join).
 * Uses getUserMedia directly — separate from the Chime adapter.
 */
export function useLocalMediaPreview({
  enabled,
  video,
  audio,
  muted,
  cameraOff,
}: Options) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (!enabled) {
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      if (videoRef.current) videoRef.current.srcObject = null
      return
    }

    let cancelled = false
    void navigator.mediaDevices
      .getUserMedia({
        video: video ? { facingMode: 'user' } : false,
        audio: audio ? { echoCancellation: true, noiseSuppression: true } : false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current?.getTracks().forEach((t) => t.stop())
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          void videoRef.current.play().catch(() => {})
        }
      })
      .catch(() => {
        /* Permissions handled upstream; preview stays on avatar fallback. */
      })

    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      if (videoRef.current) videoRef.current.srcObject = null
    }
  }, [enabled, video, audio])

  useEffect(() => {
    const stream = streamRef.current
    if (!stream) return
    stream.getAudioTracks().forEach((t) => {
      t.enabled = audio && !muted
    })
    stream.getVideoTracks().forEach((t) => {
      t.enabled = video && !cameraOff
    })
  }, [audio, video, muted, cameraOff])

  return { videoRef, streamRef }
}
